import { Controller, FieldError, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Modal, Svg, Text } from '@hopskipdrive/react-components';
import { QueryParamsForAssignRiders } from '@rideiq/organizers/types';
import { Button } from '@rideiq/shared/components/Button';
import { PageHeading } from '@rideiq/shared/components/PageHeading';
import { colorTokens } from '@rideiq/shared/components/variables';
import styled from 'styled-components';
import * as Yup from 'yup';

import { InvoiceGroupDropdown, InvoiceGroupOption } from '../../InvoiceGroupDropdown';

import { useMoveRidersToInvoiceGroup } from './useMoveRidersToInvoiceGroup';

const schema = Yup.object({
  invoiceGroup: Yup.mixed<InvoiceGroupOption>().required('Please select an invoice group'),
});

type FormValues = Yup.InferType<typeof schema>;

interface MoveToInvoiceGroupModalProps {
  open: boolean;
  selectedRiderIds: number[];
  isAllFilteredSelected: boolean;
  totalCount: number;
  onClose: () => void;
  onSuccess: () => void;
  queryParams: QueryParamsForAssignRiders;
}

export const MoveToInvoiceGroupModal = ({
  open,
  selectedRiderIds,
  isAllFilteredSelected,
  totalCount,
  onClose,
  onSuccess,
  queryParams,
}: MoveToInvoiceGroupModalProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<FormValues>({
    defaultValues: { invoiceGroup: undefined },
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const { submit, riderCount, riderLabel } = useMoveRidersToInvoiceGroup({
    selectedRiderIds,
    isAllFilteredSelected,
    totalCount,
    queryParams,
    onSuccess,
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Modal.Title>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <PageHeading variant="h1" fontWeight={700}>
            Move to Invoice Group
          </PageHeading>
          <CloseButton onClick={handleClose}>
            <Svg height="20px" icon="close-outlined" />
          </CloseButton>
        </Box>
      </Modal.Title>
      <Modal.Content>
        <Text mb={2} textColor={colorTokens.text.medium}>{`${riderCount} ${riderLabel} will be moved.`}</Text>
        <Controller
          control={control}
          name="invoiceGroup"
          render={({ field: { onChange } }) => (
            <InvoiceGroupDropdown
              onChange={onChange}
              error={!!errors.invoiceGroup}
              helperText={(errors.invoiceGroup as FieldError | undefined)?.message}
            />
          )}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button color="tertiary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit(({ invoiceGroup }) => submit(invoiceGroup!))}
          loading={isSubmitting}
        >
          {isSubmitting ? 'Moving...' : 'Move Riders'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const CloseButton = styled(Link)`
  display: inline-flex;
  margin-left: auto;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background: #ddebfa;
    svg {
      color: ${colorTokens.text.hover};
    }
  }

  &:focus-visible {
    outline: 2px solid ${colorTokens.primary.main};
    outline-offset: 2px;
  }
`;
